import React, { useEffect, useCallback } from 'react';
import { Loader, Text } from '@vibe/core';
import { useApprovalStore } from '../../stores/approvalStore';
import { getTimesheetDetail } from '../../api/timesheet-approval.api';
import TimesheetDetailHeader from './TimesheetDetailHeader';
import TimesheetWorklogList from './TimesheetWorklogList';
import TimesheetApproveRejectBar from './TimesheetApproveRejectBar';
import TimesheetRejectDialog from './TimesheetRejectDialog';
import TimesheetReopenDialog from './TimesheetReopenDialog';
import TimesheetStatusHistoryViewer from './TimesheetStatusHistoryViewer';
import type { TimesheetSubmission } from '../../types/timesheet-approval.types';

export interface TimesheetDetailPanelProps {
  currentUserId: string;
  isAdmin: boolean;
  userNameMap?: Record<string, string>;
  onActionComplete: (updated: TimesheetSubmission) => void;
}

const TimesheetDetailPanel: React.FC<TimesheetDetailPanelProps> = ({
  currentUserId,
  isAdmin,
  userNameMap,
  onActionComplete,
}) => {
  const selectedTimesheetId = useApprovalStore((s) => s.selectedTimesheetId);
  const timesheetDetail = useApprovalStore((s) => s.timesheetDetail);
  const setTimesheetDetail = useApprovalStore((s) => s.setTimesheetDetail);
  const rejectDialogOpen = useApprovalStore((s) => s.rejectDialogOpen);
  const setRejectDialogOpen = useApprovalStore((s) => s.setRejectDialogOpen);
  const reopenDialogOpen = useApprovalStore((s) => s.reopenDialogOpen);
  const setReopenDialogOpen = useApprovalStore((s) => s.setReopenDialogOpen);
  const statusHistoryDialogOpen = useApprovalStore(
    (s) => s.statusHistoryDialogOpen,
  );
  const setStatusHistoryDialogOpen = useApprovalStore(
    (s) => s.setStatusHistoryDialogOpen,
  );

  // Fetch detail when selection changes
  useEffect(() => {
    if (!selectedTimesheetId) {
      setTimesheetDetail(null);
      return;
    }

    let cancelled = false;
    getTimesheetDetail(selectedTimesheetId)
      .then((detail) => {
        if (!cancelled) setTimesheetDetail(detail);
      })
      .catch(() => {
        if (!cancelled) setTimesheetDetail(null);
      });

    return () => {
      cancelled = true;
    };
  }, [selectedTimesheetId, setTimesheetDetail]);

  const handleActionComplete = useCallback(
    (updated: TimesheetSubmission) => {
      setTimesheetDetail(updated);
      setRejectDialogOpen(false);
      setReopenDialogOpen(false);
      onActionComplete(updated);
    },
    [setTimesheetDetail, setRejectDialogOpen, setReopenDialogOpen, onActionComplete],
  );

  if (!selectedTimesheetId) {
    return (
      <div className="rp-ts-detail-panel rp-ts-detail-panel--empty">
        <Text type={Text.types.TEXT2} color={Text.colors.SECONDARY}>
          Select a timesheet to view details.
        </Text>
      </div>
    );
  }

  if (!timesheetDetail) {
    return (
      <div className="rp-ts-detail-panel rp-ts-detail-panel--loading">
        <Loader size={Loader.sizes.MEDIUM} />
      </div>
    );
  }

  return (
    <div className="rp-ts-detail-panel">
      <TimesheetDetailHeader
        submission={timesheetDetail}
        userName={userNameMap?.[timesheetDetail.userId]}
        submitterName={userNameMap?.[timesheetDetail.submittedBy]}
      />

      <TimesheetWorklogList
        userId={timesheetDetail.userId}
        periodStart={timesheetDetail.periodStart}
        periodEnd={timesheetDetail.periodEnd}
      />

      <TimesheetApproveRejectBar
        submission={timesheetDetail}
        currentUserId={currentUserId}
        isAdmin={isAdmin}
        onActionComplete={handleActionComplete}
      />

      {/* Reject dialog */}
      {rejectDialogOpen && (
        <TimesheetRejectDialog
          timesheetId={timesheetDetail.id}
          onSuccess={handleActionComplete}
          onClose={() => setRejectDialogOpen(false)}
        />
      )}

      {/* Reopen dialog */}
      {reopenDialogOpen && (
        <TimesheetReopenDialog
          timesheetId={timesheetDetail.id}
          onSuccess={handleActionComplete}
          onClose={() => setReopenDialogOpen(false)}
        />
      )}

      {/* Status history viewer */}
      {statusHistoryDialogOpen && (
        <TimesheetStatusHistoryViewer
          history={timesheetDetail.statusHistory ?? []}
          userNameMap={userNameMap}
          onClose={() => setStatusHistoryDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default TimesheetDetailPanel;
